import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { GitCompare } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PageHeader } from "@/components/PageHeader";
import { Notify, toast } from "@/components/ui/notify";
import { objLabel } from "@/features/simulations/auto/metrics";
import { useAutoBatches, useScenarios } from "@/features/simulations/hooks";
import { StartSimulation } from "./StartSimulation";
import { ScenariosTable, type HubRow } from "./ScenariosTable";

// O rótulo do objetivo vem do registry (fonte única, já segregado por kind); o mapa
// local duplicava os 3 objetivos de PARAMS e não cobriria os de sequência.
// Superfície única "Cenários salvos" fiel ao protótipo (v3-hub): cenários
// + buscas automáticas foldadas como 1 linha-resumo tagueada (nada de seções paralelas confusas).
export function SimulationsPage() {
  const navigate = useNavigate();
  const scenariosQuery = useScenarios();
  const { data: scenariosData } = scenariosQuery;
  const autoBatchesQuery = useAutoBatches();
  const { data: autoBatches = [] } = autoBatchesQuery;
  const [selected, setSelected] = useState<string[]>([]);

  const scenarios = useMemo(() => scenariosData?.items ?? [], [scenariosData]);
  // Comparação exige a MESMA linha (o backend 409a DIFFERENT_LINES): a partir de 1 selecionado, só
  // cenários da mesma linha ficam comparáveis (null == null cobre "linha nova" × "linha nova").
  const selectedLineId = useMemo(() => {
    const first = scenarios.find((s) => s.id === selected[0]);
    return selected.length > 0 ? (first?.lineId ?? null) : undefined;
  }, [selected, scenarios]);

  const rows: HubRow[] = useMemo(() => {
    const scenarioRows: HubRow[] = scenarios.map((s) => ({
      id: s.id,
      kind: "scenario",
      name: s.name,
      base: s.lineName ?? "Linha nova",
      period: s.periodLabel,
      oee: s.lastRun?.oee ?? null,
      delta: s.deltaOee,
      updatedAt: s.updatedAt,
      runStatus: s.lastRun?.status ?? null,
      confidenceLevel: s.lastRun?.confidenceLevel ?? null,
      legacy: false,
      comparable:
        s.lastRun?.status === "COMPLETED" &&
        (selectedLineId === undefined || selected.includes(s.id) || s.lineId === selectedLineId),
    }));
    const batchRows: HubRow[] = autoBatches.map((b) => ({
      id: b.id,
      kind: "auto-batch",
      // Lote de fila NÃO é otimização de parâmetros: rotular pelo kind evita
      // vender "Otimização automática" para uma comparação de sequências.
      name: `${b.kind === "SEQUENCE" ? "Sequência da fila" : "Otimização automática"} · ${objLabel(b.objective)}`,
      base: b.lineName,
      period: null,
      oee: null,
      delta: null,
      updatedAt: b.createdAt,
      runStatus: null,
      confidenceLevel: b.confidenceLevel,
      legacy: true,
      comparable: false,
    }));
    return [...scenarioRows, ...batchRows].sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
  }, [scenarios, autoBatches, selected, selectedLineId]);

  function toggleSelect(id: string) {
    setSelected((cur) =>
      cur.includes(id)
        ? cur.filter((x) => x !== id)
        : cur.length >= 2
          ? [cur[1]!, id]
          : [...cur, id]
    );
  }

  function openRow(row: HubRow) {
    navigate(
      row.kind === "auto-batch" ? `/simulations/auto/${row.id}` : `/simulations/sandbox/${row.id}`
    );
  }

  function compareSelected() {
    const runs = selected.map((id) => scenarios.find((s) => s.id === id)?.lastRun?.simulationId);
    const [a, b] = runs;
    if (!a || !b) {
      toast.error("Os dois cenários precisam ter uma rodada concluída para comparar.");
      return;
    }
    navigate(`/simulations/compare?a=${a}&b=${b}`);
  }

  return (
    <section className="space-y-5">
      <PageHeader
        title="Simulações"
        subtitle="Escolha a linha e o que você quer saber. A simulação responde a pergunta — e sugere como melhorá-la."
        actions={
          <div className="flex gap-2">
            <Button variant="outline" disabled={selected.length < 2} onClick={compareSelected}>
              <GitCompare className="h-4 w-4" />
              {selected.length >= 2 ? `Comparar (${selected.length})` : "Comparar selecionados"}
            </Button>
          </div>
        }
      />

      <StartSimulation />

      {scenariosQuery.isError || autoBatchesQuery.isError ? (
        <Notify.QueryError
          error={scenariosQuery.error ?? autoBatchesQuery.error}
          onRetry={() => {
            void scenariosQuery.refetch();
            void autoBatchesQuery.refetch();
          }}
        />
      ) : (
        <ScenariosTable
          rows={rows}
          selected={selected}
          onToggleSelect={toggleSelect}
          onOpen={openRow}
        />
      )}
    </section>
  );
}
